import React from "react";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ctaStyles } from "../Cta";
import { useReveal } from "../Reveal";

import styles from "./index.module.scss";

/** Milliseconds from the header's gate opening to each piece's reveal */
export interface SectionHeaderSequence {
    eyebrow: number;
    title: number;
    body: number;
    cta: number;
}

/** Featured Work's. Proof's (no eyebrow) is title 0, body 180, CTA 280. */
const WORK_SEQUENCE: SectionHeaderSequence = {
    eyebrow: 0,
    title: 95,
    body: 235,
    cta: 335,
};

interface Props {
    /** Rich text: an eyebrow, an h2, body paragraphs, optionally a button_link */
    value: string;
    align: "left" | "center";
    /** For the section's own layout; style children as `.yours h2` etc. */
    className?: string;
    /** Viewport fraction the headline's top crosses to reveal. Default 0.78. */
    enter?: number;
    /** Viewport fraction for the bottom-exit re-arm. Default `enter + 0.12`. */
    exit?: number;
    sequence?: Partial<SectionHeaderSequence>;
    /**
     * Set to drive the CTA's reveal from elsewhere (Expertise reveals it off
     * its circles). Left undefined, the CTA is the sequence's last step.
     */
    ctaShown?: boolean;
}

/**
 * Builds the CTA's hover roll inside the rich text's button_link. The anchor
 * comes from Wagtail as HTML, so its label can't be rendered twice by React.
 */
const addRoll = (root: HTMLElement) => {
    root.querySelectorAll("a.buttonFilled").forEach((anchor) => {
        if (anchor.querySelector(`.${ctaStyles.clip}`)) return;
        const label = anchor.textContent?.trim() ?? "";
        const clip = document.createElement("span");
        clip.className = ctaStyles.clip;
        const roll = document.createElement("span");
        roll.className = ctaStyles.roll;
        const first = document.createElement("span");
        first.textContent = label;
        const second = document.createElement("span");
        second.textContent = label;
        second.setAttribute("aria-hidden", "true");
        roll.append(first, second);
        clip.append(roll);
        anchor.replaceChildren(clip);
    });
};

/**
 * The header that opens Work, Expertise and Proof: eyebrow, headline, body and
 * CTA, revealed in sequence once the headline is in view.
 */
export const SectionHeader = (props: Props) => {
    const sequence = { ...WORK_SEQUENCE, ...props.sequence };
    const { ref, shown } = useReveal<HTMLDivElement>({
        enter: props.enter ?? 0.78,
        exit: props.exit,
        delays: [sequence.eyebrow, sequence.title, sequence.body, sequence.cta],
        target: (root) => root.querySelector("h2") ?? root,
        // The h2 is armed itself, so measure where it rests
        resting: true,
    });
    const [eyebrow, title, body, cta] = shown;
    const ctaShown = props.ctaShown ?? cta;

    React.useEffect(() => {
        if (ref.current) addRoll(ref.current);
    }, [ref, props.value]);

    return (
        <div
            ref={ref}
            className={concatClassNames([
                styles.root,
                styles[props.align],
                eyebrow ? "" : styles.preEyebrow,
                title ? "" : styles.preTitle,
                body ? "" : styles.preBody,
                ctaShown ? "" : styles.preCta,
                props.className,
            ])}
        >
            <RichTextBlock value={props.value} className={styles.content} />
        </div>
    );
};
